// src/ui/components/thumbnail-strip.js

import { LitElement, html } from "lit";
import { glassStyles } from "ui/styles/glass.css.js";
import { thumbnailsStyles } from "ui/styles/thumbnails.css.js";

export class ThumbnailStrip extends LitElement {
  static styles = [glassStyles, thumbnailsStyles];

  static properties = {
    images: { type: Array },
    selectedIndex: { type: Number },

    // virtualization state
    _start: { state: true },
    _end: { state: true },
    _thumbWidth: { state: true },
    _scrollLeft: { state: true },
  };

  constructor() {
    super();
    this.images = [];
    this.selectedIndex = 0;

    this._start = 0;
    this._end = 0;
    this._thumbWidth = 110; // must match your CSS width + gap
    this._scrollLeft = 0;

    this._posterVideo = null;
    this._scrollRaf = null;

    this._onScroll = this._onScroll.bind(this);
  }

  // ---------------------------------------------------------------------------
  // Lifecycle
  // ---------------------------------------------------------------------------

  firstUpdated() {
    const strip = this.renderRoot.querySelector(".thumb-strip");

    // Forward wheel events to horizontal scroll
    strip.addEventListener(
      "wheel",
      (e) => {
        e.preventDefault();
        strip.scrollLeft += e.deltaY;
      },
      { passive: false },
    );

    strip.addEventListener("scroll", this._onScroll);
    this._recalcWindow();
  }

  disconnectedCallback() {
    const el = this.renderRoot.querySelector(".thumb-strip");
    if (el) el.removeEventListener("scroll", this._onScroll);
    super.disconnectedCallback();
  }

  updated(changed) {
    if (changed.has("images")) {
      this._generateVideoPosters();
      this._recalcWindow();
    }
  }

  // ---------------------------------------------------------------------------
  // Virtualization
  // ---------------------------------------------------------------------------

  _onScroll(e) {
    this._scrollLeft = e.target.scrollLeft;

    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    this._scrollRaf = requestAnimationFrame(() => this._recalcWindow());
  }

  _recalcWindow() {
    const el = this.renderRoot.querySelector(".thumb-strip");
    if (!el || !this.images.length) return;

    const viewportWidth = el.clientWidth;
    const total = this.images.length;

    const start = Math.max(
      0,
      Math.floor(this._scrollLeft / this._thumbWidth) - 5,
    );

    const end = Math.min(
      total,
      Math.ceil((this._scrollLeft + viewportWidth) / this._thumbWidth) + 5,
    );

    this._start = start;
    this._end = end;

    // Mark visible items for lazy video preview creation
    for (let i = 0; i < total; i++) {
      const item = this.images[i];
      if (!item) continue;
      item._visible = i >= start && i < end;
    }

    this.requestUpdate();
  }

  // ---------------------------------------------------------------------------
  // Video poster extraction (shared video element)
  // ---------------------------------------------------------------------------

  async _generateVideoPosters() {
    if (!this.images.length) return;

    if (!this._posterVideo) {
      this._posterVideo = document.createElement("video");
      this._posterVideo.muted = true;
      this._posterVideo.playsInline = true;
      this._posterVideo.preload = "auto";
      this._posterVideo.crossOrigin = "anonymous";
      this._posterVideo.style.position = "fixed";
      this._posterVideo.style.left = "-9999px";
      this._posterVideo.style.top = "-9999px";
      document.body.appendChild(this._posterVideo);
    }

    for (const item of this.images) {
      if (item.type !== "video" || item._posterGenerated) continue;

      item._posterGenerated = true;
      const poster = await this._extractPoster(item.url);
      item._posterDataUrl = poster;

      this.requestUpdate();
    }
  }

  _extractPoster(url) {
    return new Promise((resolve) => {
      const video = this._posterVideo;

      video.onloadeddata = () => {
        try {
          video.currentTime = 0.1;
        } catch {
          resolve(null);
        }
      };

      video.onseeked = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth || 320;
        canvas.height = video.videoHeight || 180;

        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        resolve(canvas.toDataURL("image/jpeg"));
      };

      video.onerror = () => resolve(null);

      video.src = url;
      video.load();
    });
  }

  // ---------------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------------

  render() {
    const total = this.images.length;
    const visible = this.images.slice(this._start, this._end);
    const offset = this._start * this._thumbWidth;
    const totalWidth = total * this._thumbWidth;

    return html`
      <div class="thumb-strip glass-heavy ${total === 0 ? "empty" : ""}">
        <!-- Spacer defines total scrollable width -->
        <div
          class="virtual-spacer"
          style="width: ${totalWidth}px; height: 1px;"
        ></div>

        <!-- Window containing only visible thumbnails -->
        <div class="virtual-window" style="transform: translateX(${offset}px);">
          ${visible.map((item, i) => {
            const realIndex = this._start + i;
            return html`
              <div
                class="thumb ${realIndex === this.selectedIndex
                  ? "selected"
                  : ""}"
                @click=${() => this._emit("select-index", realIndex)}
              >
                ${item.type === "video"
                  ? this._renderVideoThumb(item)
                  : this._renderImageThumb(item)}
              </div>
            `;
          })}
        </div>
      </div>
    `;
  }

  _renderImageThumb(item) {
    return html`
      <img
        class="thumb-image"
        src=${item.url}
        alt=${item.name}
        loading="lazy"
      />
    `;
  }

  _renderVideoThumb(item) {
    const poster = item._posterDataUrl || "";

    return html`
      <div class="video-thumb">
        <img
          class="video-poster"
          src=${poster}
          alt=${item.name}
          loading="lazy"
        />

        ${item._visible
          ? html`
              <video
                class="video-preview"
                src=${item.url}
                muted
                playsinline
                preload="metadata"
              ></video>
            `
          : null}

        <div class="play-icon"></div>
      </div>
    `;
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  _emit(name, detail) {
    this.dispatchEvent(
      new CustomEvent(name, {
        detail,
        bubbles: true,
        composed: true,
      }),
    );
  }
}

customElements.define("thumbnail-strip", ThumbnailStrip);
